import { onMounted } from 'vue'

import { useInsightActionScope } from './useInsightActionScope'

import { insightActionsApi } from '../api/InsightActionsApi'
import { LoadHistoryFlow, LoadRunDetailFlow } from '../flows/LoadHistoryFlow'
import { PollRunFlow } from '../flows/PollRunFlow'
import { RevertFlow, cancelToken } from '../flows/RevertFlow'
import { classifyInsightActionError } from '../service/classifyInsightActionError'
import { useInsightActionStore } from '../stores/insightActionStore'

import { cancelPlatformActionRequest } from '@/features/platform-actions'

export interface UseInsightActionHistory {
  load: () => Promise<void>
  toggle: (runId: string) => Promise<void>
  revertItem: (runId: string, itemId: number) => Promise<void>
  revertRun: (runId: string) => Promise<void>
  cancelItem: (runId: string, requestId: string) => Promise<void>
}

export function useInsightActionHistory(): UseInsightActionHistory {
  const store = useInsightActionStore()
  const scope = useInsightActionScope()
  const loadHistory = new LoadHistoryFlow({ api: insightActionsApi, store })
  const loadRunDetail = new LoadRunDetailFlow({ api: insightActionsApi, store })
  const revert = new RevertFlow({ api: insightActionsApi, store })

  async function load(): Promise<void> {
    const ids = scope.forInsight(store.insightId)
    if (!ids) return
    await loadHistory.execute(ids)
  }

  async function refreshRun(runId: string): Promise<void> {
    const runScope = scope.forRun()
    if (!runScope) return
    await loadRunDetail.execute(runScope, runId, { force: true })
    await load()
    const settling = (store.runDetails[runId]?.items ?? []).some(
      (item) => item.status === 'running' || item.status === 'pending',
    )
    if (settling) void followUntilSettled(runId)
  }

  async function followUntilSettled(runId: string): Promise<void> {
    const runScope = scope.forRun()
    if (!runScope) return
    const poll = new PollRunFlow({ api: insightActionsApi, store })
    await poll.execute(runScope, store.activeKey)
    await loadRunDetail.execute(runScope, runId, { force: true })
    await load()
  }

  onMounted(() => {
    if (!store.historyLoaded) void load()
  })

  return {
    load,
    toggle: async (runId) => {
      const runScope = scope.forRun()
      const opened = store.toggleExpanded(runId)
      if (opened && runScope) await loadRunDetail.execute(runScope, runId)
    },
    revertItem: async (runId, itemId) => {
      const runScope = scope.forRun()
      if (!runScope) return
      await revert.item(runScope, runId, itemId)
      await refreshRun(runId)
    },
    revertRun: async (runId) => {
      const runScope = scope.forRun()
      if (!runScope) return
      await revert.run(runScope, runId)
      await refreshRun(runId)
    },
    cancelItem: async (runId, requestId) => {
      const workspaceId = scope.workspaceId.value
      if (workspaceId === null) return
      const token = cancelToken(requestId)
      store.setReverting(token, true)
      try {
        const result = await cancelPlatformActionRequest(workspaceId, requestId)
        if (!result.ok) store.setRevertError(token, result.error.code)
      } catch (cause) {
        store.setRevertError(token, classifyInsightActionError(cause).code)
      } finally {
        store.setReverting(token, false)
      }
      await refreshRun(runId)
    },
  }
}
